import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import Navbar from '../components/Navbar';
import { PageLoader } from '../components/LoadingSpinner';
import { toast } from 'sonner';
import { Package, Plus, ArrowLeft, Crown, Truck, Tag, Edit2, Trash2, X, Check } from 'lucide-react';

// Dropdown options
const SPORTS = ['Basketball', 'Football', 'Baseball', 'Hockey', 'Soccer', 'Golf', 'Tennis', 'UFC/MMA', 'Wrestling', 'Racing', 'Pokemon TCG', 'Yu-Gi-Oh', 'Magic: The Gathering', 'Other TCG'];
const GRADERS = ['PSA', 'BGS', 'SGC', 'CGC', 'CSG', 'HGA', 'Raw'];
const CONDITIONS = ['Mint', 'NM-MT', 'NM', 'EX', 'VG', 'Poor'];

export const MyListingsPage = () => {
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState([]);
  const [sales, setSales] = useState([]);
  const [activeTab, setActiveTab] = useState('listings');
  
  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ 
    player_name: '', 
    price: '', 
    sport: '', 
    grade_company: '',
    condition: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadData();
  }, [isAuthenticated]);

  const loadData = async () => {
    try {
      const [listingsRes, salesRes] = await Promise.all([
        api().get('/my-listings'),
        api().get('/sales')
      ]);
      setListings(listingsRes.data);
      setSales(salesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (card) => {
    setEditingId(card.id);
    setEditForm({ 
      player_name: card.player_name || '', 
      price: card.price?.toString() || '',
      sport: card.sport || '',
      grade_company: card.grade_company || '',
      condition: card.item_specifics?.raw_condition || card.item_specifics?.condition || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ player_name: '', price: '', sport: '', grade_company: '', condition: '' });
  };

  const saveEdit = async (cardId) => {
    if (!editForm.player_name.trim() || !editForm.price || !editForm.sport || !editForm.grade_company || !editForm.condition) {
      toast.error('All fields are required');
      return;
    }
    setSaving(true);
    try {
      await api().patch(`/cards/${cardId}`, {
        player_name: editForm.player_name.trim(),
        price: parseFloat(editForm.price),
        sport: editForm.sport,
        grade_company: editForm.grade_company,
        item_specifics: {
          raw_condition: editForm.condition,
          condition: editForm.grade_company === 'Raw' ? 'Raw' : 'Graded'
        }
      });
      toast.success('Listing updated');
      setEditingId(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const deleteListing = async (cardId, cardName) => {
    if (!window.confirm(`Delete "${cardName}"? This cannot be undone.`)) return;
    try {
      await api().delete(`/cards/${cardId}`);
      toast.success('Listing deleted');
      setListings(listings.filter(l => l.id !== cardId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  if (!isAuthenticated) return null;
  if (loading) return <PageLoader />;

  const pendingOrders = sales.filter(s => s.status === 'paid' && !s.tracking_number);

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="my-listings-page">
      <div className="noise-overlay" />
      <Navbar />
      
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-4xl uppercase text-white">Seller Dashboard</h1>
            <p className="text-zinc-400 mt-1">{listings.length} listings • {sales.length} orders</p>
          </div>
          
          {/* Show New Listing button if user has seller access */}
          {(user?.subscription_active || user?.membership_status === 'gold' || user?.membership_status === 'elite' || user?.membership_status === 'Creator' || user?.is_seller) ? (
            <Link to="/sell">
              <Button className="btn-primary" data-testid="new-listing-btn">
                <Plus className="w-5 h-5 mr-2" />
                New Listing
              </Button>
            </Link>
          ) : (
            <Link to="/subscription">
              <Button className="btn-primary" data-testid="subscribe-btn">
                <Crown className="w-5 h-5 mr-2" />
                Become a Seller
              </Button>
            </Link>
          )}
        </div>

        <div className="flex gap-2 mb-8 border-b border-zinc-700 pb-4">
          <Button
            variant={activeTab === 'listings' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('listings')}
            className={activeTab === 'listings' ? 'bg-blue-600' : 'text-zinc-400'}
          >
            <Tag className="w-4 h-4 mr-2" />
            Listings ({listings.length})
          </Button>
          <Button
            variant={activeTab === 'orders' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('orders')}
            className={activeTab === 'orders' ? 'bg-blue-600' : 'text-zinc-400'}
          >
            <Truck className="w-4 h-4 mr-2" />
            Orders ({sales.length})
            {pendingOrders.length > 0 && (
              <span className="ml-2 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                {pendingOrders.length}
              </span>
            )}
          </Button>
        </div>

        {activeTab === 'listings' && (
          listings.length === 0 ? (
            <div className="stat-card p-12 text-center">
              <Package className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <h2 className="text-xl font-medium text-white mb-2">No listings yet</h2>
              <p className="text-zinc-400 mb-6">Start selling your cards.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {listings.map((card) => (
                <div key={card.id} className="stat-card p-4">
                  {editingId === card.id ? (
                    /* Edit Mode */
                    <div className="space-y-3">
                      <Input
                        value={editForm.player_name}
                        onChange={(e) => setEditForm({ ...editForm, player_name: e.target.value })}
                        placeholder="Card title *"
                        className="bg-zinc-800 border-zinc-600"
                        data-testid={`edit-title-${card.id}`}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        value={editForm.price}
                        onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                        placeholder="Price *"
                        className="bg-zinc-800 border-zinc-600"
                        data-testid={`edit-price-${card.id}`}
                      />
                      <Select value={editForm.sport} onValueChange={(v) => setEditForm({ ...editForm, sport: v })}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-600" data-testid={`edit-sport-${card.id}`}>
                          <SelectValue placeholder="Sport/Category *" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-600">
                          {SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={editForm.grade_company} onValueChange={(v) => setEditForm({ ...editForm, grade_company: v })}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-600" data-testid={`edit-grader-${card.id}`}>
                          <SelectValue placeholder="Grading *" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-600">
                          {GRADERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={editForm.condition} onValueChange={(v) => setEditForm({ ...editForm, condition: v })}>
                        <SelectTrigger className="bg-zinc-800 border-zinc-600" data-testid={`edit-condition-${card.id}`}>
                          <SelectValue placeholder="Condition *" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-600">
                          {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => saveEdit(card.id)} 
                          disabled={saving}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`save-edit-${card.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          {saving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={cancelEdit}
                          className="border-zinc-600"
                          data-testid={`cancel-edit-${card.id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <>
                      <Link to={'/cards/' + card.id}>
                        <h3 className="font-medium text-white truncate hover:text-blue-400">{card.player_name}</h3>
                        <p className="text-sm text-zinc-400">{card.year} {card.set_name}</p>
                        <p className="text-xs text-zinc-500">{card.sport} • {card.grade_company}</p>
                        <p className="font-mono font-bold text-emerald-400 mt-2">${card.price?.toFixed(2)}</p>
                      </Link>
                      <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-700">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => startEdit(card)}
                          className="flex-1 border-zinc-600 hover:bg-zinc-800"
                          data-testid={`edit-btn-${card.id}`}
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => deleteListing(card.id, card.player_name)}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          data-testid={`delete-btn-${card.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )
        )}

        {activeTab === 'orders' && (
          sales.length === 0 ? (
            <div className="stat-card p-12 text-center">
              <Truck className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <h2 className="text-xl font-medium text-white mb-2">No orders yet</h2>
              <p className="text-zinc-400">Orders will appear here when someone buys your cards.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {sales.map((order) => (
                <div key={order.id} className="stat-card p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-white">{order.card_details?.player_name}</p>
                      <p className="text-sm text-zinc-400">{order.status}</p>
                    </div>
                    <p className="font-mono font-bold text-emerald-400">${order.item_price?.toFixed(2)}</p>
                  </div>
                  {order.shipping_address && (
                    <div className="mt-3 pt-3 border-t border-zinc-700">
                      <p className="text-xs text-zinc-500">Ship to:</p>
                      <p className="text-sm text-zinc-300">{order.shipping_address.name}, {order.shipping_address.city}</p>
                    </div>
                  )}
                  <div className="mt-4 flex gap-2">
                    {!order.tracking_number && order.status === 'paid' && (
                      <Button size="sm" className="bg-blue-600" onClick={() => navigate('/sales')}>
                        <Truck className="w-4 h-4 mr-2" />
                        Ship Now
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => navigate('/orders/' + order.id)}>
                      View Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </main>
    </div>
  );
};

export default MyListingsPage;
